// The end-of-form juncture's screens: what stands between the walk's last
// answer and the review. Each fills the step in the flow's voice — the
// confirm-updated-quote diff, the Base upsell, the pick-a-package prompt
// for the selection-less path, the what-we-can-do options screen — plus
// the can't-issue interrupt, which is a FlowModal because it holds a
// screen rather than being one.

import { useState } from "react";
import {
  ArrowRightIcon,
  ChevronRightIcon,
  HeartIcon,
} from "@heroicons/react/24/outline";

import Button from "components/Button";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import PlanningGuideCard from "components/PlanningGuideCard";
import ScreenChrome from "components/ScreenChrome";

import type {
  DeclineReason,
  QuotePaymentOption,
  QuotePlan,
  QuoteSelection,
  ScheduledCall,
} from "./api";
import FlowModal from "./FlowModal";
import PhoneBand from "./PhoneBand";
import PricingCards, {
  SinglePlanCards,
  soloCopy,
  defaultPaymentOption,
} from "./PricingCards";
import { dollars, monthlyScheduleValue, optionLabel } from "./payment-copy";
import SelectionBar from "./SelectionBar";

import {
  LINK_BUTTON_CLASS,
  OUTLINE_BUTTON_CLASS,
} from "components/FlowButtons";

/**
 * How the fresh offer reads against the selection the family holds —
 * computed from the one evaluate response, never from client-side rules.
 */
export type SelectionDelta =
  | { kind: "unchanged"; offered: QuotePaymentOption }
  | { kind: "price_changed"; offered: QuotePaymentOption }
  | {
      kind: "option_withdrawn";
      /** The single-pay happy path, when their plan still offers it. */
      fallback: QuotePaymentOption | null;
    }
  | { kind: "plan_unavailable" };

export function selectionDelta(
  selection: QuoteSelection,
  plans: QuotePlan[],
): SelectionDelta {
  const plan = plans.find(
    (candidate) => candidate.slug === selection.plan_slug,
  );
  if (!plan || plan.payment_options.length === 0) {
    return { kind: "plan_unavailable" };
  }
  const offered = plan.payment_options.find(
    (option) =>
      option.payment_period === selection.payment_period &&
      option.payment_period_oap === selection.payment_period_oap,
  );
  if (!offered) {
    return {
      kind: "option_withdrawn",
      fallback:
        plan.payment_options.find((option) => option.slug === "single_pay") ??
        null,
    };
  }
  const changed =
    selection.due_today_cents !== offered.due_today_cents ||
    selection.recurring_cents !== offered.recurring_cents ||
    selection.total_cents !== offered.total_cents;
  return changed
    ? { kind: "price_changed", offered }
    : { kind: "unchanged", offered };
}

function AmountTile({
  label,
  amount,
  unit,
  sub,
  emphasized,
}: {
  label: string;
  amount: string;
  unit: string;
  sub: string;
  emphasized: boolean;
}) {
  return (
    <div
      className={`flex min-w-0 flex-1 flex-col gap-1.5 rounded-xl p-5 ${
        emphasized
          ? "border-2 border-soil bg-oat"
          : "border-2 border-transparent bg-oat-50"
      }`}
    >
      <span className="text-[13px] font-bold uppercase tracking-[0.12em] text-oat-700">
        {label}
      </span>
      <div className="flex flex-wrap items-baseline gap-1.5">
        <span
          className={`text-[31px] font-bold leading-none tracking-tight sm:text-[39px] ${
            emphasized ? "text-soil" : "text-oat-700 line-through decoration-2"
          }`}
        >
          {amount}
        </span>
        <span className="text-base font-bold text-oat-700">{unit}</span>
      </div>
      <span className="text-[13px] leading-snug text-oat-700">{sub}</span>
    </div>
  );
}

function unitOf(option: QuotePaymentOption): string {
  return option.recurring_cents !== null ? "/mo" : "one payment";
}

function shownAmount(option: QuotePaymentOption): string {
  return dollars(option.recurring_cents ?? option.due_today_cents);
}

/** The frozen side of the diff, from the ledger row. */
function frozenAmount(selection: QuoteSelection): string {
  const cents = selection.recurring_cents ?? selection.due_today_cents;
  return cents === null ? "—" : dollars(cents);
}

function frozenUnit(selection: QuoteSelection): string {
  return selection.recurring_cents !== null ? "/mo" : "one payment";
}

/**
 * The mockup's payment-change screen: the quote moved under the family's
 * answers, and this is the one place it is confirmed — old amount struck
 * through, new amount standing, the facts of what stays the same.
 */
export function ConfirmChangeScreen({
  planName,
  selection,
  delta,
  busy,
  problem,
  onConfirm,
  onSeeAllPackages,
  seeAllLabel,
  onBackToAnswers,
}: {
  planName: string;
  selection: QuoteSelection;
  delta:
    | Extract<SelectionDelta, { kind: "price_changed" }>
    | (Extract<SelectionDelta, { kind: "option_withdrawn" }> & {
        fallback: QuotePaymentOption;
      });
  busy: boolean;
  problem: string | null;
  onConfirm: (option: QuotePaymentOption) => void;
  /** Absent = the way out to the full grid does not render. */
  onSeeAllPackages?: () => void;
  /** What that way out is called. Defaults to the packages wording; a
   * single-package quote has no other packages to see, but its payment
   * options are still a real choice. */
  seeAllLabel?: string;
  onBackToAnswers: () => void;
}) {
  const withdrawn = delta.kind === "option_withdrawn";
  const next = withdrawn ? delta.fallback : delta.offered;
  const copy = withdrawn
    ? {
        titleA: "Your answers mean this plan is",
        titleB: "pay-in-full only",
        blurb: `Because of one of the health answers, the carrier can't offer a monthly payment plan on the ${planName} package. You can still lock it in at today's price by paying in full — everything included stays exactly the same.`,
        oldLabel: "What you'd chosen",
        oldSub: "No longer available on this plan",
        newLabel: "What's available now",
        newSub: "Paid today. Nothing else to pay, ever.",
        note: "Nothing about the plan itself changes — same services, same price lock, same coverage. If paying in full isn't workable, our care team can talk through the alternatives.",
        cta: "Continue with pay in full",
      }
    : {
        titleA: "Your answers changed",
        titleB: "your quote",
        blurb: `Based on the answers in the application, the carrier's rate on the ${planName} package changed. Everything included stays exactly the same.`,
        oldLabel: "Your original quote",
        oldSub: "Quoted before your answers",
        newLabel: "Your updated quote",
        newSub:
          next.recurring_cents !== null
            ? "Payments start next month."
            : "One payment today.",
        note: "The package and what's included stay the same. Answers affect the rate the carrier can offer, not the plan itself.",
        cta: `Continue at ${shownAmount(next)}${
          next.recurring_cents !== null ? "/mo" : ""
        }`,
      };

  const facts: { label: string; value: string }[] = [
    { label: "Package", value: planName },
    { label: "How you're paying", value: optionLabel(next) },
    { label: "Due today", value: dollars(next.due_today_cents) },
    next.recurring_cents !== null
      ? {
          label: "Then monthly",
          value: monthlyScheduleValue(next, next.recurring_cents),
        }
      : { label: "After today", value: "Nothing, ever" },
  ];

  return (
    <ScreenChrome
      kicker="One change to your quote"
      titleA={copy.titleA}
      titleB={copy.titleB}
      blurb={copy.blurb}
    >
      <div className="mt-7 flex flex-col gap-5 rounded-2xl border-2 border-soil bg-white p-5 shadow-[0_18px_34px_-22px_rgba(80,58,58,0.4)] sm:p-7">
        <div className="flex flex-col items-stretch gap-3.5 sm:flex-row">
          <AmountTile
            label={copy.oldLabel}
            amount={frozenAmount(selection)}
            unit={frozenUnit(selection)}
            sub={copy.oldSub}
            emphasized={false}
          />
          <div className="flex flex-none rotate-90 items-center justify-center sm:rotate-0">
            <ArrowRightIcon
              className="h-5 w-5 text-oat-700"
              strokeWidth={2}
              aria-hidden="true"
            />
          </div>
          <AmountTile
            label={copy.newLabel}
            amount={shownAmount(next)}
            unit={unitOf(next)}
            sub={copy.newSub}
            emphasized
          />
        </div>
        <div className="flex flex-col gap-3">
          {facts.map((fact) => (
            <div
              key={fact.label}
              className="flex flex-col gap-0.5 border-t border-oat-200 pt-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
            >
              <span className="text-base text-oat-700">{fact.label}</span>
              <span className="text-base font-bold text-soil sm:text-right">
                {fact.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 flex items-start gap-3.5 rounded-xl bg-oat px-5 py-[18px]">
        <HeartIcon
          className="mt-0.5 h-[18px] w-[18px] shrink-0 text-evergreen"
          strokeWidth={2}
          aria-hidden="true"
        />
        <p className="text-base leading-[1.55] text-oat-700">{copy.note}</p>
      </div>

      {problem && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800">
          {problem}
        </p>
      )}

      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <button
          type="button"
          className={`${LINK_BUTTON_CLASS} self-start py-3`}
          disabled={busy}
          onClick={onBackToAnswers}
        >
          Back
        </button>
        {/* One column, both buttons the column's width — Continue level
            with Back, See-all-packages beneath it. */}
        <div className="flex flex-col gap-3 sm:items-stretch">
          <Button
            variant="continue"
            isLoading={busy}
            icon={
              <ChevronRightIcon
                className="h-4 w-4"
                strokeWidth={2.5}
                aria-hidden="true"
              />
            }
            iconPosition="right"
            onClick={() => onConfirm(next)}
          >
            {copy.cta}
          </Button>
          {onSeeAllPackages && (
            <button
              type="button"
              className={OUTLINE_BUTTON_CLASS}
              disabled={busy}
              onClick={onSeeAllPackages}
            >
              {seeAllLabel ?? "See all packages"}
            </button>
          )}
        </div>
      </div>
    </ScreenChrome>
  );
}

/**
 * The selection-less pricing-cards screen: the full grid for a family that
 * never chose (or whose choice fell away), with the phone band's
 * reassurance beneath. The Base upsell wears its own screen (UpsellScreen).
 */
export function ChoosePlanScreen({
  kicker,
  titleA,
  titleB,
  blurb,
  plans,
  includedInAllFeatures,
  singlePlan,
  initialSelection,
  phoneBand,
  busy,
  problem,
  onChoose,
  onBackToAnswers,
}: {
  kicker: string;
  titleA: string;
  titleB: string;
  blurb: string;
  plans: QuotePlan[];
  includedInAllFeatures: QuotePlan["features"] | null;
  /** Present the one package as the plan, exactly as the quote screen
   * does — this screen renders the same cards, so it inherits the same
   * question about whether a comparison is being implied. */
  singlePlan: boolean;
  initialSelection: { plan_slug: string; option_slug: string } | null;
  /** Present = the phone band renders under the cards: the booked call's
   * reassurance, or the way to get one. */
  phoneBand?: {
    bookedCall: ScheduledCall | null;
    contactPhone: string | null;
    onSchedule: () => void;
  };
  busy: boolean;
  problem: string | null;
  onChoose: (choice: { plan_slug: string; option_slug: string }) => void;
  onBackToAnswers: () => void;
}) {
  const solePlan = singlePlan && plans.length === 1 ? plans[0] : null;
  // Same reasoning as the quote screen: with no Choose button, an
  // initial selection naming a package or option this face does not
  // offer cannot be corrected by the customer, so it is not honoured.
  const savedFitsSole =
    solePlan !== null &&
    initialSelection !== null &&
    initialSelection.plan_slug === solePlan.slug &&
    solePlan.payment_options.some(
      (o) => o.slug === initialSelection.option_slug,
    );
  const soleFallback = solePlan && !savedFitsSole ? solePlan : null;
  const [planSlug, setPlanSlug] = useState<string | null>(
    soleFallback?.slug ?? initialSelection?.plan_slug ?? null,
  );
  const [optionSlug, setOptionSlug] = useState<string | null>(
    soleFallback
      ? (defaultPaymentOption(soleFallback)?.slug ?? null)
      : (initialSelection?.option_slug ?? null),
  );

  const chosenPlan = plans.find((p) => p.slug === planSlug) ?? null;
  const chosenOption =
    chosenPlan?.payment_options.find((o) => o.slug === optionSlug) ?? null;

  // The same sticky bar as the quote screen carries the Continue, so it
  // stays in reach however far down the cards the family has read.
  return (
    <div className="flex flex-1 flex-col">
      <ScreenChrome
        kicker={kicker}
        titleA={titleA}
        titleB={titleB}
        blurb={blurb}
        wide
      >
        <div className="mt-7">
          {solePlan ? (
            <SinglePlanCards
              plan={solePlan}
              baselineFeatures={includedInAllFeatures}
              chosenOptionSlug={optionSlug}
              onSelectOption={(option) => {
                setPlanSlug(solePlan.slug);
                setOptionSlug(option.slug);
              }}
            />
          ) : (
            <PricingCards
              plans={plans}
              includedInAllFeatures={includedInAllFeatures}
              selectedPlanSlug={planSlug}
              chosenOptionSlug={optionSlug}
              onSelectPlan={(slug) => {
                // Choosing a package chooses its default way to pay.
                const chosenPlan = plans.find((p) => p.slug === slug);
                setPlanSlug(slug);
                setOptionSlug(
                  chosenPlan
                    ? (defaultPaymentOption(chosenPlan)?.slug ?? null)
                    : null,
                );
              }}
              onSelectOption={(slug, option) => {
                setPlanSlug(slug);
                setOptionSlug(option.slug);
              }}
            />
          )}
        </div>

        <div className="mt-8">
          <button
            type="button"
            className={LINK_BUTTON_CLASS}
            disabled={busy}
            onClick={onBackToAnswers}
          >
            Back
          </button>
        </div>

        {phoneBand && (
          <div className="mt-8">
            <PhoneBand
              bookedCall={phoneBand.bookedCall}
              contactPhone={phoneBand.contactPhone}
              onSchedule={phoneBand.onSchedule}
            />
          </div>
        )}
      </ScreenChrome>
      <SelectionBar
        plan={chosenPlan}
        option={chosenOption}
        title={
          solePlan && chosenPlan
            ? soloCopy(chosenPlan.slug).barTitle
            : undefined
        }
        busy={busy}
        error={problem}
        onContinue={onChoose}
      />
    </div>
  );
}

/** The decline slugs read aloud — one line per cause, deliberately
 * plain: the framing around them stays generic, whatever the cause. */
const DECLINE_REASON_COPY: Record<DeclineReason["kind"], string> = {
  out_of_area:
    "The address is outside the areas where we can currently issue policies.",
  age: "The date of birth falls outside the age range our payment plans cover.",
  carrier_answers: "Your answers to the carrier\u2019s required questions.",
  terminal: "The answers about current health and life expectancy.",
};

/** The generic framing is FOREVER, not an interim: never per-cause voice
 * in the heading. The causes render as a plain list inside the content —
 * several can apply at once. */
function DeclineReasonList({ reasons }: { reasons: DeclineReason[] }) {
  if (reasons.length === 0) return null;
  return (
    <ul className="flex flex-col gap-2.5">
      {reasons.map((reason) => (
        <li
          key={reason.kind}
          className="rounded-xl bg-oat px-4 py-3.5 text-base leading-[1.55] text-soil"
        >
          {DECLINE_REASON_COPY[reason.kind]}
        </li>
      ))}
    </ul>
  );
}

/**
 * The can't-issue interrupt: based on the answers, no policy can be
 * issued. Shown at a Continue press, never mid-typing (R16), and the
 * family stays where they are — a corrected answer re-checks. "See
 * other options" leads; the answers are usually not a mistake, so
 * updating them is the second path, not the first.
 */
export function CantIssueModal({
  reasons,
  onSeeOtherOptions,
  onUpdateAnswers,
  onClose,
}: {
  /** The distinct causes, when the evaluation named them — null renders
   * the generic framing alone. */
  reasons: DeclineReason[] | null;
  onSeeOtherOptions: () => void;
  onUpdateAnswers: () => void;
  onClose: () => void;
}) {
  return (
    <FlowModal icon={HeartIcon} title="Your application" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <h2 className="font-serif text-[25px] leading-tight text-soil sm:text-[31px]">
          Based on your answers, we&rsquo;re unable to{" "}
          <span className="font-sans font-bold text-evergreen">
            issue a policy
          </span>
        </h2>
        {reasons !== null && <DeclineReasonList reasons={reasons} />}
        <p className="text-lg leading-[1.55] text-oat-700">
          That doesn&rsquo;t end the planning — there are still ways we can
          help. And if something was mistyped — a date of birth, an address —
          update it and we&rsquo;ll take another look.
        </p>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="button"
            className={CONTINUE_BUTTON_CLASS}
            onClick={onSeeOtherOptions}
          >
            See other options
            <ChevronRightIcon
              className="h-4 w-4"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            className={OUTLINE_BUTTON_CLASS}
            onClick={onUpdateAnswers}
          >
            Update answers
          </button>
        </div>
      </div>
    </FlowModal>
  );
}

/** The promise we make ONLY when the address is the whole story. */
function OutOfAreaRemark() {
  return (
    <p className="px-1 text-base leading-[1.55] text-oat-700">
      We&rsquo;re opening in new areas all the time — when we reach yours,
      we&rsquo;ll contact you.
    </p>
  );
}

/**
 * The what-we-can-do screen for an address outside the areas we issue
 * in. It stands where the quiz or the quote screen would have — nothing
 * in the flow applies past it, so it offers no way back into it.
 */
export function OutOfAreaScreen({
  onPlanningGuideClicked,
}: {
  /** The guide link is an outbound, new-tab navigation — nothing else
   * marks that it happened, so this is the only signal. */
  onPlanningGuideClicked?: () => void;
}) {
  return (
    <ScreenChrome
      kicker="Your quote"
      titleA="Here's what"
      titleB="we can do"
      blurb="We can't issue a pre-need policy for your address yet — but these options are open to you."
    >
      <div className="mt-8 flex max-w-[620px] flex-col gap-4">
        <PlanningGuideCard onClick={onPlanningGuideClicked} />
        <OutOfAreaRemark />
      </div>
    </ScreenChrome>
  );
}

/**
 * The what-we-can-do screen: what stands behind the can't-issue
 * modal's "See other options", and where an ineligible quote's reload
 * lands. Framing stays generic whatever the causes; the causes only
 * shape the quiet out-of-area line.
 */
export function OtherOptionsScreen({
  reasons,
  onBackToApplication,
  onTypeformClicked,
}: {
  /** Null until a fresh evaluation lands (the reload path re-verifies in
   * the background). */
  reasons: DeclineReason[] | null;
  onBackToApplication: () => void;
  /** The free-planning-guide link is an outbound, new-tab navigation —
   * nothing else marks that it happened, so this is the only signal. */
  onTypeformClicked?: () => void;
}) {
  const outOfAreaAlone =
    reasons !== null &&
    reasons.length === 1 &&
    reasons[0].kind === "out_of_area";
  return (
    <ScreenChrome
      kicker="Your application"
      titleA="Here's what"
      titleB="we can do"
      blurb="Based on your answers, we're unable to issue you a pre-need policy at this time. Everything you shared is saved — and these options are open to you."
    >
      <div className="mt-8 flex max-w-[620px] flex-col gap-4">
        <PlanningGuideCard onClick={onTypeformClicked} />
        {outOfAreaAlone && <OutOfAreaRemark />}
        <div className="mt-2">
          <button
            type="button"
            className={LINK_BUTTON_CLASS}
            onClick={onBackToApplication}
          >
            Back to the application
          </button>
        </div>
      </div>
    </ScreenChrome>
  );
}
